/**
 * SQL fragments for admin opportunities table tabs ("פתוחות" / "לא טופלו").
 * Mirrors `server/src/entities/opportunity/opportunity-untreated-list-sql.ts` for Remult rawFilter.
 *
 * Group parents are classified by child instances, not synced parent dates alone.
 * A row counts as open while `is_full` is false. Seat totals are not counted here:
 * that count is a correlated subquery and timed out the table at 30s.
 */

export function opportunityParentHasActiveChildExistsSql(parentIdRef: string): string {
  return `EXISTS (
    SELECT 1
    FROM "opportunity" AS child_opp
    WHERE child_opp.parent_opportunity_id = ${parentIdRef}
      AND child_opp.is_deleted = false
  )`;
}

/** Child still inside the registration window. Ignores remaining seats. */
export function opportunityChildDateOpenExistsSql(
  parentIdRef: string,
  nowParam: string,
): string {
  return `EXISTS (
    SELECT 1
    FROM "opportunity" AS child_opp
    WHERE child_opp.parent_opportunity_id = ${parentIdRef}
      AND child_opp.is_deleted = false
      AND child_opp.is_closed = false
      AND child_opp.is_full = false
      AND child_opp.last_call_to_volunteers > ${nowParam}
      AND (
        child_opp.start_date IS NULL
        OR child_opp.start_date >= ${nowParam}
      )
      AND (
        child_opp.end_date IS NULL
        OR child_opp.end_date >= ${nowParam}
      )
  )`;
}

/** Child in the registration window that is not marked full. */
export function opportunityChildStillOpenExistsSql(
  parentIdRef: string,
  nowParam: string,
): string {
  return `EXISTS (
    SELECT 1
    FROM "opportunity" AS child_opp
    WHERE child_opp.parent_opportunity_id = ${parentIdRef}
      AND child_opp.is_deleted = false
      AND child_opp.is_closed = false
      AND child_opp.is_full = false
      AND child_opp.last_call_to_volunteers > ${nowParam}
      AND (
        child_opp.start_date IS NULL
        OR child_opp.start_date >= ${nowParam}
      )
      AND (
        child_opp.end_date IS NULL
        OR child_opp.end_date >= ${nowParam}
      )
  )`;
}

export function opportunityRowFutureOpenFieldsSql(
  tableRef: string,
  nowParam: string,
): string {
  return `(
    ${tableRef}.is_deleted = false
    AND ${tableRef}.is_closed = false
    AND ${tableRef}.is_full = false
    AND ${tableRef}.last_call_to_volunteers > ${nowParam}
    AND (
      ${tableRef}.start_date IS NULL
      OR ${tableRef}.start_date >= ${nowParam}
    )
    AND (
      ${tableRef}.end_date IS NULL
      OR ${tableRef}.end_date >= ${nowParam}
    )
  )`;
}

export function opportunityRowUntreatedOverdueFieldsSql(
  tableRef: string,
  nowParam: string,
): string {
  return `(
    ${tableRef}.is_deleted = false
    AND ${tableRef}.is_closed = false
    AND ${tableRef}.is_full = false
    AND (
      ${tableRef}.last_call_to_volunteers <= ${nowParam}
      OR (
        ${tableRef}.start_date IS NOT NULL
        AND ${tableRef}.start_date <= ${nowParam}
      )
      OR (
        ${tableRef}.end_date IS NOT NULL
        AND ${tableRef}.end_date <= ${nowParam}
      )
    )
  )`;
}

function groupChildRowFutureOpenSql(tableRef: string, nowParam: string): string {
  return `(
    ${tableRef}.parent_opportunity_id IS NOT NULL
    AND ${opportunityRowFutureOpenFieldsSql(tableRef, nowParam)}
  )`;
}

function groupChildRowUntreatedOverdueSql(
  tableRef: string,
  nowParam: string,
): string {
  return `(
    ${tableRef}.parent_opportunity_id IS NOT NULL
    AND ${opportunityRowUntreatedOverdueFieldsSql(tableRef, nowParam)}
  )`;
}

function groupParentFutureOpenSql(tableRef: string, nowParam: string): string {
  return `(
    ${tableRef}.parent_opportunity_id IS NULL
    AND ${tableRef}.is_deleted = false
    AND ${tableRef}.is_closed = false
    AND ${opportunityChildStillOpenExistsSql(`${tableRef}.id`, nowParam)}
  )`;
}

function standaloneFutureOpenSql(tableRef: string, nowParam: string): string {
  return `(
    ${tableRef}.parent_opportunity_id IS NULL
    AND NOT ${opportunityParentHasActiveChildExistsSql(`${tableRef}.id`)}
    AND ${opportunityRowFutureOpenFieldsSql(tableRef, nowParam)}
  )`;
}

function groupParentUntreatedSql(tableRef: string, nowParam: string): string {
  return `(
    ${tableRef}.parent_opportunity_id IS NULL
    AND ${tableRef}.is_deleted = false
    AND ${tableRef}.is_closed = false
    AND ${opportunityParentHasActiveChildExistsSql(`${tableRef}.id`)}
    AND NOT ${opportunityChildDateOpenExistsSql(`${tableRef}.id`, nowParam)}
  )`;
}

function standaloneUntreatedSql(tableRef: string, nowParam: string): string {
  return `(
    ${tableRef}.parent_opportunity_id IS NULL
    AND NOT ${opportunityParentHasActiveChildExistsSql(`${tableRef}.id`)}
    AND ${opportunityRowUntreatedOverdueFieldsSql(tableRef, nowParam)}
  )`;
}

export type OpportunitiesTableTabSqlRowScope = "roots" | "all";

/** "פתוחות" tab signals – group parents follow open child instances. */
export function buildFutureOpenTabSignalsSql(
  tableRef: string,
  nowParam: string,
  rowScope: OpportunitiesTableTabSqlRowScope = "roots",
): string {
  const rootBranches = [
    groupParentFutureOpenSql(tableRef, nowParam),
    standaloneFutureOpenSql(tableRef, nowParam),
  ];

  if (rowScope === "all") {
    return `(
      ${groupChildRowFutureOpenSql(tableRef, nowParam)}
      OR ${rootBranches.join("\n      OR ")}
    )`;
  }

  return `(${rootBranches.join(" OR ")})`;
}

/** "לא טופלו" tab signals – group parents only when every active child is overdue. */
export function buildUntreatedTabSignalsSql(
  tableRef: string,
  nowParam: string,
  rowScope: OpportunitiesTableTabSqlRowScope = "roots",
): string {
  const rootBranches = [
    groupParentUntreatedSql(tableRef, nowParam),
    standaloneUntreatedSql(tableRef, nowParam),
  ];

  if (rowScope === "all") {
    return `(
      ${groupChildRowUntreatedOverdueSql(tableRef, nowParam)}
      OR ${rootBranches.join("\n      OR ")}
    )`;
  }

  return `(${rootBranches.join(" OR ")})`;
}
